/**
 * Blog Post 17: HOS Mortgage Guide 2026: Complete Calculation & Application Strategy
 * 2026居屋按揭完整指南：計算方法及申請攻略
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import AmazonRecommendation from '../components/common/AmazonRecommendation';
import SEO from '../components/common/SEO';

export default function BlogPost17() {
    const { i18n } = useCustomTranslation();
    const isEnglish = i18n.language === 'en';

    useEffect(() => {
        trackPageView('/blog/hos-mortgage-guide-hong-kong-2026', 'Blog Post 17');
        document.title = isEnglish
            ? 'HOS Mortgage Guide 2026: Complete Calculation & Application Strategy | PayWise365'
            : '2026居屋按揭完整指南：計算方法及申請攻略 | PayWise365';
    }, [isEnglish]);

    const seoTitle = isEnglish
        ? 'HOS Mortgage Guide 2026: Complete Calculation & Application Strategy | PayWise365'
        : '2026居屋按揭完整指南：計算方法及申請攻略 | PayWise365';

    const seoDescription = isEnglish
        ? 'Complete HOS mortgage guide 2026. Learn calculation methods, maximum loan amounts, interest rates, application process, and key differences from private property mortgages. Includes real examples and step-by-step strategies for Hong Kong buyers.'
        : '2026居屋按揭全面指南。了解計算方法、最高貸款額、利率、申請流程，以及與私樓按揭的關鍵分別。包含實例計算及香港買家逐步攻略。';

    const seoKeywords = isEnglish
        ? 'HOS mortgage, 居屋按揭, HOS loan calculator, green form mortgage, white form mortgage, HOS interest rate, 居屋按揭計算, home ownership scheme, subsidized housing mortgage, HOS vs private mortgage'
        : '居屋按揭, 綠表按揭, 白表按揭, 居屋貸款, 居屋按揭計算, 資助房屋按揭, 居屋利率, 房協居屋, 居屋二手市場, 白居二';

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={seoKeywords}
                canonicalUrl="https://www.paywise365.com/blog/hos-mortgage-guide-hong-kong-2026"
                ogType="article"
                ogImage="https://www.paywise365.com/logo.svg"
                publishDate="2026-04-10T00:00:00+08:00"
                modifiedDate="2026-04-10T00:00:00+08:00"
                author="PayWise365"
                articleSection={isEnglish ? 'HOS & Subsidized Housing' : '居屋及資助房屋'}
                tags={isEnglish
                    ? ['HOS Mortgage', 'Green Form', 'White Form', 'Subsidized Housing', 'First Time Buyer', 'Hong Kong Property']
                    : ['居屋按揭', '綠表', '白表', '資助房屋', '首次置業', '香港樓市']
                }
            />
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    color: 'white',
                    py: { xs: 6, md: 8 },
                }}
            >
                <Container maxWidth="lg">
                    {/* Breadcrumb Navigation */}
                    <Box sx={{ mb: 3, fontSize: '14px', opacity: 0.9 }}>
                        <a href="/" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Home' : '首頁'}
                        </a>
                        {' > '}
                        <a href="/blog" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Blog' : '資訊'}
                        </a>
                        {' > '}
                        <span style={{ opacity: 0.8 }}>
                            {isEnglish ? 'HOS Mortgage Guide' : '居屋按揭指南'}
                        </span>
                    </Box>

                    <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                        {isEnglish
                            ? 'HOS Mortgage Guide 2026: Complete Calculation & Application Strategy'
                            : '2026居屋按揭完整指南：計算方法及申請攻略'}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
                        {isEnglish
                            ? 'Master HOS mortgage calculations and secure the best rates for subsidized housing'
                            : '掌握居屋按揭計算，取得資助房屋最優惠利率'}
                    </Typography>
                </Container>
            </Box>

            {/* Article Content */}
            <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
                <Box
                    component="article"
                    sx={{
                        lineHeight: 1.8,
                        '& p': { mb: 2 },
                        '& h2': { mt: 4, mb: 2, fontSize: '1.75rem', fontWeight: 700 },
                        '& h3': { mt: 3, mb: 1.5, fontSize: '1.5rem', fontWeight: 600 },
                        '& ul, & ol': { mb: 2, pl: 3 },
                        '& li': { mb: 1 },
                    }}
                >
                    {!isEnglish ? (
                        // Traditional Chinese Content
                        <>
                            <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                                    <span style={{
                                        background: '#d1fae5',
                                        color: '#059669',
                                        padding: '6px 14px',
                                        borderRadius: '14px',
                                        fontWeight: '500',
                                        marginRight: '12px'
                                    }}>居屋及資助房屋</span>
                                    <span>發布日期：2026年4月10日</span>
                                    <span style={{ margin: '0 8px' }}>•</span>
                                    <span>閱讀時間：16 分鐘</span>
                                </div>
                            </div>

                            <h2>🏘️ 引言：居屋——香港首次置業的最佳跳板</h2>
                            <p>
                                在香港，私樓樓價高企，對於首次置業人士來說，居屋（Home Ownership Scheme，HOS）是一個較為實惠的選擇。
                                一個位於沙田的私樓可能要HKD 8M，但同區的居屋可能只需HKD 4M-5M，相差接近一半！
                            </p>
                            <p>
                                不過，居屋按揭與私樓按揭有很多不同之處，包括：
                            </p>
                            <ul>
                                <li><strong>按揭成數更高：</strong>最高可達95%（綠表）或90%（白表），毋須按揭保險</li>
                                <li><strong>利率可能較高：</strong>部分銀行對居屋收取較高利率</li>
                                <li><strong>轉售限制：</strong>首5年（一手）或2年（二手）不能自由轉售</li>
                                <li><strong>擔保期問題：</strong>影響按揭年期及成數</li>
                            </ul>
                            <p>
                                本文將為你全面拆解居屋按揭的所有細節，幫助你計算準確的供款金額，並取得最優惠的按揭條件。
                            </p>

                            <h2>📚 居屋按揭基礎知識</h2>
                            <h3>什麼是居屋？</h3>
                            <p>
                                居屋是香港房屋委員會（房委會）或香港房屋協會（房協）推出的<strong>資助出售房屋計劃</strong>，
                                旨在協助中低收入家庭以較廉價購買自住物業。居屋售價一般為市價的52%-70%不等。
                            </p>

                            <h3>綠表 vs 白表：有什麼分別？</h3>
                            <div style={{
                                background: '#f0fdf4',
                                border: '2px solid #86efac',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#065f46' }}>✅ 綠表申請人（公屋居民）</h4>
                                <p><strong>適用對象：</strong></p>
                                <ul>
                                    <li>現時居住在香港房屋委員會公屋的租戶</li>
                                    <li>持有房屋署簽發有效《綠表資格證明書》人士</li>
                                    <li>房委會「長者租金津貼計劃」受惠者</li>
                                </ul>
                                <p><strong>按揭優勢：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li><strong>最高按揭成數：95%</strong>（只需付5%首期）</li>
                                    <li>毋須通過壓力測試（部分銀行）</li>
                                    <li>利率通常較低</li>
                                </ul>
                            </div>

                            <div style={{
                                background: '#fafaf9',
                                border: '2px solid #d4d4d8',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#18181b' }}>⚪ 白表申請人（非公屋居民）</h4>
                                <p><strong>適用對象：</strong></p>
                                <ul>
                                    <li>香港永久性居民，未持有任何物業</li>
                                    <li>符合入息及資產限額</li>
                                    <li>大部分首次置業人士</li>
                                </ul>
                                <p><strong>按揭條件：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li><strong>最高按揭成數：90%</strong>（需付10%首期）</li>
                                    <li>需通過壓力測試</li>
                                    <li>利率可能略高於綠表</li>
                                </ul>
                            </div>

                            <h2>💰 居屋按揭成數及首期計算</h2>
                            <h3>一手居屋（新盤）</h3>
                            <p>由房委會或房協首次出售的全新居屋單位。</p>

                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px',
                                overflowX: 'auto'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #cbd5e1', background: '#f1f5f9' }}>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>申請類別</th>
                                            <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>最高按揭成數</th>
                                            <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>最低首期</th>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>HKD 4M例子</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f0fdf4' }}>
                                            <td style={{ padding: '10px 8px' }}>綠表</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#059669', fontWeight: '600' }}>95%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#059669', fontWeight: '600' }}>5%</td>
                                            <td style={{ padding: '10px 8px' }}>首期：HKD 200,000<br />貸款：HKD 3,800,000</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px' }}>白表</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#0891b2', fontWeight: '600' }}>90%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#0891b2', fontWeight: '600' }}>10%</td>
                                            <td style={{ padding: '10px 8px' }}>首期：HKD 400,000<br />貸款：HKD 3,600,000</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div style={{
                                background: '#dbeafe',
                                border: '1px solid #93c5fd',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p style={{ fontWeight: '600', color: '#1e40af', marginBottom: '10px' }}>💡 重要優勢</p>
                                <p style={{ marginBottom: 0 }}>
                                    居屋按揭<strong>毋須購買按揭保險</strong>，即使按揭成數高達90%-95%。
                                    這是因為政府為居屋提供30年擔保（房委會）或25年（房協），
                                    銀行風險較低，因此接受高成數按揭而不收取額外保險費。
                                </p>
                            </div>

                            <h3>二手居屋（白居二/二手市場）</h3>
                            <p>
                                二手居屋的按揭成數受<strong>政府擔保期</strong>影響。房委會居屋由首次出售日期起計30年擔保，
                                擔保期越長，按揭成數越高。
                            </p>

                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #fcd34d',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#78350f' }}>📅 擔保期計算公式</h4>
                                <p style={{ background: 'white', padding: '12px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '15px', textAlign: 'center' }}>
                                    剩餘擔保期 = 30年 - （現時年份 - 首次出售年份）
                                </p>
                                <p><strong>例子：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>2016年出售的居屋，在2026年購買</li>
                                    <li>樓齡：2026 - 2016 = 10年</li>
                                    <li>剩餘擔保期：30 - 10 = <strong>20年</strong></li>
                                </ul>
                            </div>

                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px',
                                overflowX: 'auto'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #cbd5e1', background: '#f1f5f9' }}>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>剩餘擔保期</th>
                                            <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>綠表最高成數</th>
                                            <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>白表最高成數</th>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>按揭年期</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f0fdf4' }}>
                                            <td style={{ padding: '10px 8px' }}>≥20年</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#059669', fontWeight: '600' }}>95%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#0891b2', fontWeight: '600' }}>90%</td>
                                            <td style={{ padding: '10px 8px' }}>最長25年</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px' }}>19年</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>90-95%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>85-90%</td>
                                            <td style={{ padding: '10px 8px' }}>最長19年</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px' }}>10-18年</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>80-90%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>70-85%</td>
                                            <td style={{ padding: '10px 8px' }}>與擔保期相約</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px' }}>&lt;10年</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#dc2626' }}>60%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#dc2626' }}>60%</td>
                                            <td style={{ padding: '10px 8px' }}>最長10年</td>
                                        </tr>
                                        <tr style={{ background: '#fef2f2' }}>
                                            <td style={{ padding: '10px 8px' }}>已過擔保期</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#991b1b', fontWeight: '600' }}>60%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#991b1b', fontWeight: '600' }}>60%</td>
                                            <td style={{ padding: '10px 8px' }}>按私樓標準</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <h2>📊 居屋按揭利率比較（2026年4月）</h2>
                            <p>居屋按揭利率與私樓相若，但部分銀行可能提供特優利率給綠表申請人。</p>

                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px',
                                overflowX: 'auto'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #cbd5e1', background: '#f1f5f9' }}>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>銀行</th>
                                            <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>一手居屋</th>
                                            <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>二手居屋（擔保期內）</th>
                                            <th style={{ textAlign: 'center', padding: '12px 8px', fontWeight: '600' }}>現金回贈</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>滙豐銀行</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#059669' }}>P - 2.5% = 3.375%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>P - 2.5% = 3.375%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>1%</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>恒生銀行</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#059669' }}>P - 2.5% = 3.375%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>P - 2.5% = 3.375%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>1%</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>中國銀行</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px', color: '#059669' }}>P - 2.5% = 3.375%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>P - 2.5% = 3.375%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>1%</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>渣打銀行</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>P - 2.35% = 3.525%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>P - 2.35% = 3.525%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>0.5%</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>東亞銀行</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>P - 2.3% = 3.575%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>P - 2.3% = 3.575%</td>
                                            <td style={{ textAlign: 'center', padding: '10px 8px' }}>0.5%</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p style={{ fontSize: '12px', color: '#64748b', marginTop: '15px', marginBottom: 0 }}>
                                    註：P = 最優惠利率（Prime Rate），現時大部分銀行為5.875%<br />
                                    利率會因應申請人資格、貸款額及還款年期而有所不同
                                </p>
                            </div>

                            <h2>💵 實例計算：每月供款金額</h2>

                            <h3>例子1：綠表購買一手居屋</h3>
                            <div style={{
                                background: '#f0fdf4',
                                border: '2px solid #86efac',
                                borderRadius: '8px',
                                padding: '25px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>物業詳情：</strong></p>
                                <ul>
                                    <li>居屋售價：HKD 4,000,000</li>
                                    <li>申請類別：綠表</li>
                                    <li>按揭成數：95%</li>
                                    <li>貸款金額：HKD 3,800,000</li>
                                    <li>首期：HKD 200,000</li>
                                    <li>利率：P - 2.5% = 3.375%</li>
                                    <li>還款年期：25年</li>
                                </ul>
                                <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginTop: '15px' }}>
                                    <p style={{ fontSize: '15px', marginBottom: '8px' }}><strong>每月供款：</strong></p>
                                    <p style={{ fontSize: '32px', fontWeight: '700', color: '#059669', margin: '0' }}>
                                        HKD 18,933
                                    </p>
                                </div>
                                <p style={{ marginTop: '15px', marginBottom: 0 }}>
                                    <strong>總還款額：</strong>HKD 5,679,900<br />
                                    <strong>總利息支出：</strong>HKD 1,879,900
                                </p>
                            </div>

                            <h3>例子2：白表購買一手居屋</h3>
                            <div style={{
                                background: '#fafaf9',
                                border: '2px solid #d4d4d8',
                                borderRadius: '8px',
                                padding: '25px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>物業詳情：</strong></p>
                                <ul>
                                    <li>居屋售價：HKD 4,000,000</li>
                                    <li>申請類別：白表</li>
                                    <li>按揭成數：90%</li>
                                    <li>貸款金額：HKD 3,600,000</li>
                                    <li>首期：HKD 400,000</li>
                                    <li>利率：P - 2.5% = 3.375%</li>
                                    <li>還款年期：25年</li>
                                </ul>
                                <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginTop: '15px' }}>
                                    <p style={{ fontSize: '15px', marginBottom: '8px' }}><strong>每月供款：</strong></p>
                                    <p style={{ fontSize: '32px', fontWeight: '700', color: '#18181b', margin: '0' }}>
                                        HKD 17,935
                                    </p>
                                </div>
                                <p style={{ marginTop: '15px', marginBottom: 0 }}>
                                    <strong>總還款額：</strong>HKD 5,380,500<br />
                                    <strong>總利息支出：</strong>HKD 1,780,500<br />
                                    <span style={{ color: '#059669', fontWeight: '600' }}>比綠表多付首期：HKD 200,000（但總利息少HKD 99,400）</span>
                                </p>
                            </div>

                            <h3>例子3：購買二手居屋（樓齡10年，剩餘擔保期20年）</h3>
                            <div style={{
                                background: '#eff6ff',
                                border: '2px solid #93c5fd',
                                borderRadius: '8px',
                                padding: '25px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>物業詳情：</strong></p>
                                <ul>
                                    <li>售價：HKD 5,500,000</li>
                                    <li>首次出售年份：2016年</li>
                                    <li>樓齡：10年</li>
                                    <li>剩餘擔保期：20年</li>
                                    <li>申請類別：白表</li>
                                    <li>按揭成數：90%</li>
                                    <li>貸款金額：HKD 4,950,000</li>
                                    <li>首期：HKD 550,000</li>
                                    <li>利率：P - 2.5% = 3.375%</li>
                                    <li>還款年期：20年（配合擔保期）</li>
                                </ul>
                                <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginTop: '15px' }}>
                                    <p style={{ fontSize: '15px', marginBottom: '8px' }}><strong>每月供款：</strong></p>
                                    <p style={{ fontSize: '32px', fontWeight: '700', color: '#0891b2', margin: '0' }}>
                                        HKD 28,435
                                    </p>
                                </div>
                                <p style={{ marginTop: '15px', marginBottom: 0 }}>
                                    <strong>總還款額：</strong>HKD 6,824,400<br />
                                    <strong>總利息支出：</strong>HKD 1,874,400
                                </p>
                            </div>

                            <h2>📝 居屋按揭申請流程</h2>

                            <h3>步驟1：確認資格及選擇單位</h3>
                            <ol>
                                <li>確定自己屬於綠表或白表申請人</li>
                                <li>參加居屋抽籤或在二手市場選擇單位</li>
                                <li>簽署臨時買賣合約（支付臨時訂金，通常為5%）</li>
                            </ol>

                            <h3>步驟2：準備文件及申請按揭</h3>
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>所需文件：</strong></p>
                                <ul>
                                    <li>香港身份證</li>
                                    <li>最近3個月住址證明（水電煤單、銀行月結單）</li>
                                    <li><strong>入息證明：</strong>
                                        <ul>
                                            <li>受僱：最近3個月糧單、最近年度稅單、強積金供款記錄</li>
                                            <li>自僱：最近2年稅單、公司營業證明、銀行流水</li>
                                        </ul>
                                    </li>
                                    <li>臨時買賣合約副本</li>
                                    <li>綠表／白表資格證明文件</li>
                                    <li>居屋申請表副本（如適用）</li>
                                </ul>
                            </div>

                            <h3>步驟3：銀行審批（約2-3星期）</h3>
                            <ul>
                                <li>銀行審查申請人入息、信貸記錄</li>
                                <li>進行物業估價</li>
                                <li>白表申請人需通過壓力測試（綠表部分銀行豁免）</li>
                                <li>發出按揭批核信（Offer Letter）</li>
                            </ul>

                            <h3>步驟4：簽署正式買賣合約及按揭契約</h3>
                            <ul>
                                <li>在律師樓簽署正式買賣合約</li>
                                <li>簽署銀行按揭契約</li>
                                <li>支付印花稅（居屋享有較低稅率或豁免）</li>
                            </ul>

                            <h3>步驟5：收樓及銀行放款</h3>
                            <ul>
                                <li>銀行將貸款直接支付給賣方律師</li>
                                <li>買方支付餘下樓價（首期 - 臨時訂金）</li>
                                <li>收樓及開始供款</li>
                            </ul>

                            <h2>⚖️ 居屋 vs 私樓按揭：關鍵分別</h2>

                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px',
                                overflowX: 'auto'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #cbd5e1', background: '#f1f5f9' }}>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>項目</th>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>居屋按揭</th>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>私樓按揭</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>最高按揭成數</td>
                                            <td style={{ padding: '10px 8px', color: '#059669' }}>綠表95%，白表90%<br />（毋須按保）</td>
                                            <td style={{ padding: '10px 8px' }}>60-70%<br />（需按保可達80-90%）</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>按揭保險費</td>
                                            <td style={{ padding: '10px 8px', color: '#059669' }}>毋須</td>
                                            <td style={{ padding: '10px 8px' }}>需要（成數&gt;60%）<br />約1.15%-5%貸款額</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>壓力測試</td>
                                            <td style={{ padding: '10px 8px' }}>白表需要<br />綠表部分銀行豁免</td>
                                            <td style={{ padding: '10px 8px' }}>必須通過</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>利率</td>
                                            <td style={{ padding: '10px 8px' }}>P - 2.3%至2.5%<br />（約3.375%-3.575%）</td>
                                            <td style={{ padding: '10px 8px' }}>P - 2.5%至2.75%<br />（約3.125%-3.375%）</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>最長還款期</td>
                                            <td style={{ padding: '10px 8px' }}>25年（受擔保期限制）</td>
                                            <td style={{ padding: '10px 8px' }}>30年</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>轉按</td>
                                            <td style={{ padding: '10px 8px' }}>首5年（一手）或2年（二手）<br />需徵收補地價才可轉按</td>
                                            <td style={{ padding: '10px 8px' }}>隨時可轉按</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>出租</td>
                                            <td style={{ padding: '10px 8px', color: '#dc2626' }}>禁止<br />（未補地價前）</td>
                                            <td style={{ padding: '10px 8px', color: '#059669' }}>可以</td>
                                        </tr>
                                        <tr style={{ background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px', fontWeight: '500' }}>轉售</td>
                                            <td style={{ padding: '10px 8px' }}>首5年只可在居二市場轉售<br />或需補地價</td>
                                            <td style={{ padding: '10px 8px' }}>隨時可自由轉售</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <h2>❓ 常見問題解答</h2>

                            <h3>Q1: 綠表申請人一定要通過壓力測試嗎？</h3>
                            <p>
                                <strong>A:</strong> 不一定。部分銀行對綠表申請人豁免壓力測試，但仍需符合<strong>供款比率不超過50%</strong>的要求。
                                即每月供款不可超過家庭入息的50%。白表申請人則必須通過壓力測試。
                            </p>

                            <h3>Q2: 可以用父母或配偶做擔保人嗎？</h3>
                            <p>
                                <strong>A:</strong> 可以。如果申請人入息不足以通過壓力測試，可以加入擔保人（通常為配偶或父母）。
                                擔保人的入息會一併計算，但按揭成數可能會因而降低，視乎擔保人是否已有其他物業按揭。
                            </p>

                            <h3>Q3: 購買已過擔保期的二手居屋，按揭如何處理？</h3>
                            <p>
                                <strong>A:</strong> 已過政府擔保期（30年）的居屋，銀行會按<strong>私樓標準</strong>處理按揭申請：<br />
                                • 最高按揭成數：60%（綠表、白表一樣）<br />
                                • 需通過壓力測試<br />
                                • 如需更高成數，要購買按揭保險<br />
                                • 按揭年期受樓齡限制（75 - 樓齡 或 30年，取較短者）
                            </p>

                            <h3>Q4: 居屋按揭可以享有現金回贈嗎？</h3>
                            <p>
                                <strong>A:</strong> 可以。大部分銀行對居屋按揭提供0.5%-1%現金回贈，與私樓相若。
                                以HKD 4M貸款計，1%回贈即HKD 40,000。回贈通常在貸款提取後1-2個月存入申請人戶口。
                            </p>

                            <h3>Q5: 如果我先買居屋，日後想換私樓，按揭如何處理？</h3>
                            <p>
                                <strong>A:</strong> 有兩個方案：<br />
                                <strong>方案1：補地價後出售／出租</strong><br />
                                • 繳付補價（市價 - 原價的差額折現）<br />
                                • 補價後變成私樓，可自由買賣、出租<br />
                                • 可用補價後的居屋加按套現，作為買私樓首期<br />
                                <br />
                                <strong>方案2：在居二市場出售（毋須補價）</strong><br />
                                • 持有滿5年後可在居屋第二市場轉售<br />
                                • 買家必須為綠表或白表人士<br />
                                • 售價受政府限制，通常低於市價
                            </p>

                            <h3>Q6: 綠表資格證明書有期限嗎？</h3>
                            <p>
                                <strong>A:</strong> 有。綠表資格證明書有效期為<strong>12個月</strong>。
                                如果在有效期內未能成功購買居屋，需要重新申請。部分情況下（如公屋租戶）可在購買時才申請綠表。
                            </p>

                            <h3>Q7: 購買房協居屋跟房委會居屋有什麼分別？</h3>
                            <p>
                                <strong>A:</strong> 主要分別：<br />
                                <strong>政府擔保期：</strong><br />
                                • 房委會居屋：30年擔保<br />
                                • 房協居屋：25年擔保<br />
                                <br />
                                <strong>轉售限制：</strong><br />
                                • 房委會：首5年禁售（一手），2年（二手）<br />
                                • 房協：首15年只可售予房協提名的買家，之後可自由轉售（毋須補價）<br />
                                <br />
                                <strong>按揭處理：</strong>基本相同，但房協居屋擔保期較短，影響按揭年期及成數
                            </p>

                            <h3>Q8: 我可以同時申請多間銀行的按揭嗎？</h3>
                            <p>
                                <strong>A:</strong> 可以，而且<strong>強烈建議</strong>這樣做。申請多間銀行按揭的好處：<br />
                                • 比較利率及回贈，選擇最優惠方案<br />
                                • 分散風險（如某銀行拒批）<br />
                                • 增加議價能力（可用其他銀行offer要求減息）<br />
                                <br />
                                建議同時向3-5間銀行申請，取得按揭批核信後再作最終選擇。
                            </p>

                            <h2>🎯 總結及建議</h2>
                            <div style={{
                                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                border: '2px solid #10b981',
                                borderRadius: '12px',
                                padding: '30px',
                                marginBottom: '30px'
                            }}>
                                <h3 style={{ marginTop: 0, color: '#065f46' }}>💡 居屋按揭關鍵要點</h3>
                                <ul style={{ marginBottom: '20px' }}>
                                    <li><strong>高按揭成數優勢</strong>——綠表95%、白表90%，毋須按保費</li>
                                    <li><strong>擔保期很重要</strong>——影響按揭成數、年期及利率</li>
                                    <li><strong>綠表比白表優惠</strong>——更高成數、部分銀行豁免壓測</li>
                                    <li><strong>二手居屋要計樓齡</strong>——剩餘擔保期決定按揭條件</li>
                                    <li><strong>轉售有限制</strong>——首5年不能自由轉售或出租</li>
                                </ul>

                                <h3 style={{ color: '#065f46' }}>📝 申請前準備清單</h3>
                                <ol style={{ marginBottom: 0 }}>
                                    <li>確認自己的綠表/白表資格</li>
                                    <li>計算自己的負擔能力（收入 × 50% ÷ 0.035 大約可借貸額）</li>
                                    <li>使用<a href="/calculators/house-loan" style={{ color: '#047857', textDecoration: 'underline', fontWeight: '600' }}>按揭計算機</a>估算每月供款</li>
                                    <li>預留首期資金（5%-10%樓價）</li>
                                    <li>準備齊全文件（糧單、稅單、身份證等）</li>
                                    <li>同時向3-5間銀行申請，爭取最優惠條件</li>
                                    <li>考慮長遠計劃（會否日後換私樓？）</li>
                                </ol>
                            </div>

                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #fcd34d',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '30px'
                            }}>
                                <p style={{ fontWeight: '600', color: '#78350f', marginBottom: '10px' }}>⚠️ 免責聲明</p>
                                <p style={{ fontSize: '13px', marginBottom: 0 }}>
                                    本文提供的資訊僅供參考，不構成財務或法律建議。
                                    居屋按揭政策及銀行條款可能隨時變更，實際按揭條件應以銀行最新公布及個別審批為準。
                                    在作出任何財務決定前，請諮詢專業按揭顧問或銀行職員。
                                </p>
                            </div>

                            {/* Internal Links */}
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '30px'
                            }}>
                                <h3 style={{ fontSize: '18px', marginTop: 0 }}>📚 相關文章</h3>
                                <ul style={{ marginBottom: 0, listStyle: 'none', paddingLeft: 0 }}>
                                    <li style={{ marginBottom: '10px' }}>
                                        🏠 <a href="/blog/first-time-homebuyer-mortgage-guide-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            首次置業按揭全攻略
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        💰 <a href="/blog/hong-kong-stamp-duty-guide-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            香港印花稅完整指南
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        📊 <a href="/blog/rent-vs-buy-hong-kong-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            租樓vs買樓完整財務分析
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        🏦 <a href="/blog/2026-hong-kong-best-mortgage-rates" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            2026年香港最優惠按揭利率比較
                                        </a>
                                    </li>
                                    <li>
                                        🔄 <a href="/blog/mortgage-refinancing-hong-kong-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            按揭轉按全攻略
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            {/* CTA Section */}
                            <div style={{
                                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                color: 'white',
                                borderRadius: '12px',
                                padding: '30px',
                                textAlign: 'center',
                                marginBottom: '40px'
                            }}>
                                <h3 style={{ marginTop: 0, fontSize: '24px' }}>🧮 計算你的居屋按揭供款</h3>
                                <p style={{ fontSize: '16px', marginBottom: '20px', opacity: 0.95 }}>
                                    使用我們的免費計算機，準確計算每月供款、利息支出及還款時間表
                                </p>
                                <a
                                    href="/calculators/house-loan"
                                    style={{
                                        background: 'white',
                                        color: '#059669',
                                        padding: '12px 30px',
                                        borderRadius: '8px',
                                        textDecoration: 'none',
                                        fontWeight: '600',
                                        fontSize: '16px',
                                        display: 'inline-block'
                                    }}
                                >
                                    免費按揭計算機 →
                                </a>
                            </div>
                        </>
                    ) : (
                        // English Content
                        <>
                            <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                                    <span style={{
                                        background: '#d1fae5',
                                        color: '#059669',
                                        padding: '6px 14px',
                                        borderRadius: '14px',
                                        fontWeight: '500',
                                        marginRight: '12px'
                                    }}>HOS & Subsidized Housing</span>
                                    <span>Published: April 10, 2026</span>
                                    <span style={{ margin: '0 8px' }}>•</span>
                                    <span>Reading Time: 16 min</span>
                                </div>
                            </div>

                            <h2>🏘️ Introduction: HOS—The Best Springboard for First-Time Hong Kong Buyers</h2>
                            <p>
                                In Hong Kong, private property prices are extremely high. For first-time buyers, the Home Ownership Scheme (HOS)
                                offers a more affordable alternative. A private flat in Sha Tin might cost HKD 8M, while a comparable HOS flat
                                in the same area could be just HKD 4M-5M—nearly half the price!
                            </p>
                            <p>
                                However, HOS mortgages differ significantly from private property mortgages:
                            </p>
                            <ul>
                                <li><strong>Higher loan-to-value ratio:</strong> Up to 95% (Green Form) or 90% (White Form), no mortgage insurance required</li>
                                <li><strong>Possibly higher interest rates:</strong> Some banks charge higher rates for HOS</li>
                                <li><strong>Resale restrictions:</strong> Cannot freely resell within first 5 years (new) or 2 years (resale)</li>
                                <li><strong>Guarantee period matters:</strong> Affects mortgage term and LTV ratio</li>
                            </ul>

                            <h2>📚 HOS Mortgage Basics</h2>
                            <h3>Green Form vs White Form</h3>

                            <div style={{
                                background: '#f0fdf4',
                                border: '2px solid #86efac',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#065f46' }}>✅ Green Form (Public Housing Tenants)</h4>
                                <p><strong>Maximum LTV:</strong> 95% (only 5% down payment)</p>
                                <p><strong>Advantages:</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>No stress test required (most banks)</li>
                                    <li>Lower interest rates</li>
                                    <li>Lowest down payment requirement</li>
                                </ul>
                            </div>

                            <div style={{
                                background: '#fafaf9',
                                border: '2px solid #d4d4d8',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#18181b' }}>⚪ White Form (Non-Public Housing Residents)</h4>
                                <p><strong>Maximum LTV:</strong> 90% (10% down payment required)</p>
                                <p><strong>Requirements:</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>Must pass stress test</li>
                                    <li>Slightly higher interest rates</li>
                                    <li>Most first-time buyers fall into this category</li>
                                </ul>
                            </div>

                            <h2>💰 Monthly Payment Examples</h2>

                            <h3>Example 1: Green Form Buying New HOS</h3>
                            <div style={{
                                background: '#f0fdf4',
                                border: '2px solid #86efac',
                                borderRadius: '8px',
                                padding: '25px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>Property Details:</strong></p>
                                <ul>
                                    <li>HOS Price: HKD 4,000,000</li>
                                    <li>LTV: 95%</li>
                                    <li>Loan: HKD 3,800,000</li>
                                    <li>Down payment: HKD 200,000</li>
                                    <li>Interest rate: 3.375%</li>
                                    <li>Term: 25 years</li>
                                </ul>
                                <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginTop: '15px' }}>
                                    <p style={{ fontSize: '15px', marginBottom: '8px' }}><strong>Monthly Payment:</strong></p>
                                    <p style={{ fontSize: '32px', fontWeight: '700', color: '#059669', margin: '0' }}>
                                        HKD 18,933
                                    </p>
                                </div>
                                <p style={{ marginTop: '15px', marginBottom: 0 }}>
                                    <strong>Total repayment:</strong> HKD 5,679,900<br />
                                    <strong>Total interest:</strong> HKD 1,879,900
                                </p>
                            </div>

                            <h2>❓ Frequently Asked Questions</h2>

                            <h3>Q1: Must Green Form applicants pass stress test?</h3>
                            <p>
                                <strong>A:</strong> Not necessarily. Most banks waive stress test for Green Form applicants,
                                but still require <strong>debt servicing ratio ≤50%</strong>. White Form applicants must pass stress test.
                            </p>

                            <h3>Q2: What if I buy HOS beyond government guarantee period?</h3>
                            <p>
                                <strong>A:</strong> HOS beyond 30-year guarantee is treated as <strong>private property</strong>:<br />
                                • Maximum LTV: 60% (both Green and White Form)<br />
                                • Stress test required<br />
                                • Mortgage insurance needed for higher LTV<br />
                                • Mortgage term limited by property age
                            </p>

                            <h3>Q3: Can I get cash rebate for HOS mortgage?</h3>
                            <p>
                                <strong>A:</strong> Yes. Most banks offer 0.5%-1% cash rebate for HOS mortgages,
                                similar to private property. For HKD 4M loan, 1% rebate = HKD 40,000.
                            </p>

                            <h2>🎯 Summary & Recommendations</h2>
                            <div style={{
                                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                border: '2px solid #10b981',
                                borderRadius: '12px',
                                padding: '30px',
                                marginBottom: '30px'
                            }}>
                                <h3 style={{ marginTop: 0, color: '#065f46' }}>💡 Key Takeaways</h3>
                                <ul style={{ marginBottom: '20px' }}>
                                    <li><strong>High LTV advantage</strong>—Green Form 95%, White Form 90%, no mortgage insurance</li>
                                    <li><strong>Guarantee period crucial</strong>—affects LTV, term, and interest rates</li>
                                    <li><strong>Green Form better terms</strong>—higher LTV, stress test waiver at some banks</li>
                                    <li><strong>Resale restrictions</strong>—cannot freely sell within first 5 years</li>
                                </ul>

                                <h3 style={{ color: '#065f46' }}>📝 Pre-Application Checklist</h3>
                                <ol>
                                    <li>Confirm Green/White Form eligibility</li>
                                    <li>Calculate affordability (Income × 50% ÷ 0.035 ≈ loan capacity)</li>
                                    <li>Use <a href="/calculators/house-loan" style={{ color: '#047857', textDecoration: 'underline', fontWeight: '600' }}>mortgage calculator</a> to estimate monthly payments</li>
                                    <li>Reserve down payment (5%-10% of price)</li>
                                    <li>Prepare all documents (payslips, tax returns, ID)</li>
                                    <li>Apply to 3-5 banks simultaneously for best terms</li>
                                </ol>
                            </div>

                            {/* Internal Links */}
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '30px'
                            }}>
                                <h3 style={{ fontSize: '18px', marginTop: 0 }}>📚 Related Articles</h3>
                                <ul style={{ marginBottom: 0, listStyle: 'none', paddingLeft: 0 }}>
                                    <li style={{ marginBottom: '10px' }}>
                                        🏠 <a href="/blog/first-time-homebuyer-mortgage-guide-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            First-Time Homebuyer Mortgage Guide
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        💰 <a href="/blog/hong-kong-stamp-duty-guide-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            Hong Kong Stamp Duty Complete Guide
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        📊 <a href="/blog/rent-vs-buy-hong-kong-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            Rent vs Buy Complete Analysis
                                        </a>
                                    </li>
                                    <li>
                                        🏦 <a href="/blog/2026-hong-kong-best-mortgage-rates" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            Hong Kong Best Mortgage Rates 2026
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            {/* CTA Section */}
                            <div style={{
                                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                color: 'white',
                                borderRadius: '12px',
                                padding: '30px',
                                textAlign: 'center',
                                marginBottom: '40px'
                            }}>
                                <h3 style={{ marginTop: 0, fontSize: '24px' }}>🧮 Calculate Your HOS Mortgage Payments</h3>
                                <p style={{ fontSize: '16px', marginBottom: '20px', opacity: 0.95 }}>
                                    Use our free calculator to accurately estimate monthly payments, interest costs, and amortization schedule
                                </p>
                                <a
                                    href="/calculators/house-loan"
                                    style={{
                                        background: 'white',
                                        color: '#059669',
                                        padding: '12px 30px',
                                        borderRadius: '8px',
                                        textDecoration: 'none',
                                        fontWeight: '600',
                                        fontSize: '16px',
                                        display: 'inline-block'
                                    }}
                                >
                                    Free Mortgage Calculator →
                                </a>
                            </div>
                        </>
                    )}
                </Box>

                {/* Amazon Recommendation */}
                <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #e2e8f0' }}>
                    <AmazonRecommendation isEnglish={isEnglish} />
                </Box>
            </Container>
        </Box>
    );
}
